import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  LinearProgress,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Error as ErrorIcon,
  Science,
} from '@mui/icons-material';
import { viewsAPI } from '../services/api';
import { ViewEnrichmentJobStatus, ViewEnrichmentRowStatus } from '../types';

// ── Props ─────────────────────────────────────────────────────────────────────

interface EnrichmentPageProps {
  viewId: number;
  viewName: string;
  jobId: number;
  onBack: () => void;
  onComplete: (rows: ViewEnrichmentRowStatus[]) => void;
}

const POLL_INTERVAL_MS = 3000;

// ── Component ─────────────────────────────────────────────────────────────────

const EnrichmentPage: React.FC<EnrichmentPageProps> = ({
  viewId,
  viewName,
  jobId,
  onBack,
  onComplete,
}) => {
  const [job, setJob] = useState<ViewEnrichmentJobStatus | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await viewsAPI.getEnrichmentStatus(viewId, jobId);
      setJob(data);
      if (data.status === 'completed' || data.status === 'failed') {
        stopPolling();
      }
    } catch (err: any) {
      setPollError(err.message || 'Failed to fetch enrichment status');
      stopPolling();
    }
  }, [viewId, jobId, stopPolling]);

  useEffect(() => {
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return stopPolling;
  }, [fetchStatus, stopPolling]);

  const isJobDone = job?.status === 'completed' || job?.status === 'failed';
  const total     = job?.total_rows     ?? 0;
  const completed = job?.completed_rows ?? 0;
  const failed    = job?.failed_rows    ?? 0;
  const pending   = Math.max(0, total - completed - failed);
  const progressPct = total > 0 ? Math.round(((completed + failed) / total) * 100) : 0;

  const completedRows = job?.rows.filter(r => r.status === 'completed') ?? [];

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#E5E7EB' }}>

      {/* ── Page Header ── */}
      <Box sx={{
        p: 3,
        backgroundColor: '#283E56',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      }}>
        <IconButton onClick={onBack} sx={{ color: 'white' }}>
          <ArrowBack />
        </IconButton>
        <Science sx={{ fontSize: 28 }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Full Enrichment Search
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            {viewName}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>

        {pollError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPollError(null)}>
            {pollError}
          </Alert>
        )}

        {/* ── Loading before first poll ── */}
        {!job && !pollError && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#283E56' }} />
          </Box>
        )}

        {/* ── Progress card ── */}
        {job && (
          <Paper elevation={2} sx={{ px: 2.5, py: 2, borderRadius: 2, mb: 3 }}>

            {/* Row 1: icon + title | status chip */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!isJobDone
                  ? <CircularProgress size={18} sx={{ color: '#283E56' }} />
                  : job.status === 'completed'
                  ? <CheckCircle sx={{ color: '#22c55e', fontSize: 20 }} />
                  : <ErrorIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                }
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#283E56' }}>
                  {!isJobDone
                    ? 'Enrichment in progress…'
                    : job.status === 'completed'
                    ? 'Enrichment complete'
                    : 'Enrichment finished with errors'}
                </Typography>
              </Box>
              <Chip
                label={job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                size="small"
                sx={{
                  fontWeight: 600, fontSize: '0.72rem',
                  ...(job.status === 'completed' ? { backgroundColor: '#dcfce7', color: '#166534' }
                    : job.status === 'running'   ? { backgroundColor: '#eff6ff', color: '#1d4ed8' }
                    : job.status === 'failed'    ? { backgroundColor: '#fef2f2', color: '#991b1b' }
                    :                              { backgroundColor: '#F3F4F6', color: '#6B7280' }),
                }}
              />
            </Box>

            {/* Row 1b: stat summary */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, pl: '28px' }}>
              {[
                { label: 'total',     value: total },
                { label: 'completed', value: completed, color: completed > 0 ? '#16a34a' : undefined },
                { label: 'failed',    value: failed,    color: failed > 0 ? '#dc2626' : undefined },
                { label: 'pending',   value: pending },
              ].map((s, i) => (
                <React.Fragment key={s.label}>
                  {i > 0 && <Typography sx={{ color: '#D1D5DB', fontSize: '0.78rem', mx: 0.25 }}>·</Typography>}
                  <Typography sx={{ fontSize: '0.78rem', color: '#9CA3AF' }}>
                    <Box component="span" sx={{ fontWeight: 700, color: s.color ?? '#374151' }}>{s.value}</Box>
                    {' '}{s.label}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>

            {/* Row 2: progress bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: isJobDone ? 1.5 : 0 }}>
              <LinearProgress
                variant="determinate"
                value={progressPct}
                sx={{
                  flex: 1, height: 6, borderRadius: 99,
                  backgroundColor: '#E5E7EB',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: job.status === 'failed' ? '#ef4444' : '#00C853',
                    borderRadius: 99,
                  },
                }}
              />
              <Typography sx={{
                fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap', minWidth: 32, textAlign: 'right',
                color: job.status === 'failed' ? '#dc2626' : progressPct === 100 ? '#16a34a' : '#283E56',
              }}>
                {progressPct}%
              </Typography>
            </Box>

            {/* Row 3: action button (only when done and there are completed rows) */}
            {isJobDone && completedRows.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, pt: 1.5, borderTop: '1px solid #E5E7EB' }}>
                <Button
                  variant="contained"
                  onClick={() => onComplete(completedRows)}
                  sx={{
                    backgroundColor: '#00C853', fontWeight: 700, px: 2, boxShadow: 'none',
                    '&:hover': { backgroundColor: '#009624', boxShadow: 'none' },
                  }}
                >
                  View Results ({completedRows.length})
                </Button>
              </Box>
            )}
          </Paper>
        )}

        {/* ── Per-row status table ── */}
        {job && job.rows.length > 0 && (
          <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, py: 1.5, backgroundColor: '#283E56', color: 'white' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Member Status
              </Typography>
            </Box>
            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {job.rows.map(r => {
                const statusColor =
                  r.status === 'completed' ? '#16a34a'
                  : r.status === 'failed'  ? '#dc2626'
                  : r.status === 'running' ? '#1d4ed8'
                  : '#9CA3AF';
                const statusBg =
                  r.status === 'completed' ? '#f0fdf4'
                  : r.status === 'failed'  ? '#fef2f2'
                  : r.status === 'running' ? '#eff6ff'
                  : '#F9FAFB';
                return (
                  <Box
                    key={r.id}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      px: 2.5, py: 1, borderBottom: '1px solid #F3F4F6',
                      backgroundColor: statusBg,
                    }}
                  >
                    <Typography sx={{ fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>
                      {[r.first_name, r.last_name].filter(Boolean).join(' ')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {r.status === 'running' && <CircularProgress size={12} sx={{ color: '#1d4ed8' }} />}
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: statusColor }}>
                        {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                      </Typography>
                      {r.error_message && (
                        <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          — {r.error_message}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default EnrichmentPage;
